import { appendToInboxReminders, replaceMatchingReminder } from '../actions/inboxRemindersActions';
import getInboxReminderDraft from '../selectors/getInboxReminderDraft';
import isNewReminder from '../utils/isNewReminder';
import { CalendarItemTypeTypeEx } from 'owa-calendar-types/lib/types/CalendarEvent';
import getDefaultReminder from 'owa-calendar-forms-common/lib/utils/getDefaultReminder';
import { getGuid } from 'owa-guid';
import EmailReminderChangeType from 'owa-service/lib/contract/EmailReminderChangeType';
import EmailReminderSendOption from 'owa-service/lib/contract/EmailReminderSendOption';
import InboxReminderType from 'owa-service/lib/contract/InboxReminderType';
import { orchestrator } from 'satcheljs';
import {
    addNewEmailReminder,
    editInboxReminder,
    discardDraftInboxReminder,
    saveDraftInboxReminder,
    setDraftInboxReminder,
    setDraftInboxReminderOffset,
} from '../actions/draftInboxReminderActions';
import { setCustomReminderDate } from '../actions/customReminderDateActions';
import getCustomReminderDate from '../selectors/getCustomReminderDate';
import { calculateOffsetFromOwaDates } from '../utils/owaDateToReminderOffsetConverter';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';

const enum ReminderActionType {
    Add,
    Edit,
}

orchestrator(addNewEmailReminder, actionMessage => {
    const { isAllDay, isOrganizerReminder, calendarItemType } = actionMessage;

    // 1. Create new inbox reminder with client guid
    const newInboxReminder: InboxReminderType = {
        Id: getGuid(),
        ReminderOffset: getDefaultReminder(isAllDay),
        Message: '',
        IsOrganizerReminder: isOrganizerReminder,
        OccurrenceChange: getOccurrenceChange(calendarItemType, ReminderActionType.Add),
        SendOption: EmailReminderSendOption.User,
    };

    // 2. Set the draft inbox reminder
    setDraftInboxReminder(newInboxReminder);
});

orchestrator(editInboxReminder, actionMessage => {
    const { inboxReminder, calendarItemType } = actionMessage;

    // 1. Clone the inbox reminder
    const clonedDraftReminder = { ...inboxReminder };

    // 2. Update the OccurrenceChange
    clonedDraftReminder.OccurrenceChange = getOccurrenceChange(
        calendarItemType,
        ReminderActionType.Edit
    );

    // 3. Set the cloned reminder as the draft
    setDraftInboxReminder(clonedDraftReminder);
});

orchestrator(saveDraftInboxReminder, actionMessage => {
    const { event, entrySource } = actionMessage;

    const draftReminder = getInboxReminderDraft();
    updateDraftReminderOffsetIfNecessary(event);

    const inboxReminders = event.InboxReminders;

    // 1. If saving a new reminder, append it to the inboxReminders list
    if (isNewReminder(draftReminder, inboxReminders)) {
        appendToInboxReminders(draftReminder, event, entrySource);
    } else {
        // otherwise replace the existing reminder with the draft
        replaceMatchingReminder(draftReminder, event, entrySource);
    }

    // 2. Clear the draft reminder
    setDraftInboxReminder(null);

    // 3. Clear the custom reminder time if necessary
    setCustomReminderDate(null);
});

orchestrator(discardDraftInboxReminder, () => {
    // Clear the draft reminder disregard if it's a new reminder or existing
    setDraftInboxReminder(null);

    // Clear the custom reminder date
    setCustomReminderDate(null);
});

function getOccurrenceChange(
    calendarItemType: CalendarItemTypeTypeEx,
    reminderAction: ReminderActionType
): EmailReminderChangeType {
    switch (calendarItemType) {
        case 'Occurrence':
        case 'Exception':
            if (reminderAction === ReminderActionType.Add) {
                return EmailReminderChangeType.Added;
            } else if (reminderAction === ReminderActionType.Edit) {
                // If this is a series reminder that we are now changing for a specific occurrence, mark the reminder as modified.
                return EmailReminderChangeType.Override;
            } else {
                return EmailReminderChangeType.None;
            }

        default:
            return EmailReminderChangeType.None;
    }
}

function updateDraftReminderOffsetIfNecessary(calendarEvent: CalendarEvent) {
    // In case it is a custom email reminder, convert the custom owa date to reminder ReminderOffset
    const customReminderDate = getCustomReminderDate();
    if (customReminderDate !== null) {
        const startDate = calendarEvent.Start;
        const reminderOffset = calculateOffsetFromOwaDates(customReminderDate, startDate);
        setDraftInboxReminderOffset(reminderOffset);
    }
}
